import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import Select from '@/components/forms/Select'
import { CreatableInput } from '@/components/forms/CreatableInput'
import { TextInput } from '@/components/forms/TextInput'
import { Loader } from '@/components/loaders/Loader'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { fetchTaxonomyOptions } from '@/reducers/playbooks/playbook.actions'

import { BOOLEAN_TYPE_KEY, getRowTypeKey, isLogicField, PICK_LIST_TYPE_KEY } from '../helpers'
import {
  BOOLEAN_OPERAND_OPTIONS,
  EDGE_OPERATOR,
  getGateBlockedReason,
  getOperatorsForSource,
  getPendingOptionChoices,
  hasStagedOptions,
  operatorNeedsValue,
  parseOperandList,
  serializeOperandList,
} from '../helpers/fieldLinks.helpers'

// Gating a field on another field's value: pick the source, the comparison, and
// what to compare against. Only sources the target may legally depend on are
// offered (see getEligibleSources), and the operator list is filtered by the
// source's type, so the summary service's save-time edge validators are
// unreachable from here.
//
// @param {object} props.targetField the prompt field being gated
// @param {object[]} props.sourceOptions prompt fields this one may depend on
// @param {Set<string>} [props.inheritedTaxonomyKeys] normalized names of the
//   fields whose options are the org default's — offered but not selectable,
//   see getGateBlockedReason
// @param {object} [props.pendingTaxonomies] option edits applied for testing but
//   not saved, keyed by taxonomy uuid — offered ahead of the saved taxonomy
// @param {object} [props.existingLink] the link being edited, absent when adding
export const FieldConditionModal = ({
  show,
  onClose,
  onSave,
  onRemove,
  targetField,
  sourceOptions,
  inheritedTaxonomyKeys,
  pendingTaxonomies = {},
  existingLink,
}) => {
  const dispatch = useDispatch()

  const [sourceKey, setSourceKey] = useState('')
  const [operator, setOperator] = useState(EDGE_OPERATOR.eq)
  const [compareValue, setCompareValue] = useState('')
  const [selectionOptions, setSelectionOptions] = useState([])
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [optionsErrored, setOptionsErrored] = useState(false)

  // Re-seed whenever the modal opens, so editing one chip then another doesn't
  // carry the first one's draft over.
  useEffect(() => {
    if (!show) {
      return
    }
    if (isNil(existingLink)) {
      const firstSelectable = sourceOptions.find((field) =>
        isNil(getGateBlockedReason(field, inheritedTaxonomyKeys))
      )
      setSourceKey(firstSelectable?.key ?? '')
      setOperator(EDGE_OPERATOR.eq)
      setCompareValue('')
      return
    }
    setSourceKey(existingLink.source)
    setOperator(existingLink.operator ?? EDGE_OPERATOR.eq)
    setCompareValue(existingLink.compare_value ?? '')
  }, [show, existingLink])

  const sourceField = sourceOptions.find((field) => field.key === sourceKey)
  // A source can be legal to depend on and still be unusable as a gate — its
  // options may be the org default's. That can only be reached by editing a
  // condition written before the row went back to the default, since the picker
  // won't select one.
  const blockedReason = getGateBlockedReason(sourceField, inheritedTaxonomyKeys)
  const isBlockedSource = !isNil(blockedReason)
  // With every candidate blocked the picker opens on nothing, so the warning has
  // to stand on its own rather than wait for a selection that can't be made.
  const everySourceBlocked =
    !isEmpty(sourceOptions) &&
    sourceOptions.every((field) => !isNil(getGateBlockedReason(field, inheritedTaxonomyKeys)))
  const sourceTypeKey = isNil(sourceField) ? null : getRowTypeKey(sourceField)
  const isSelectionSource = sourceTypeKey === PICK_LIST_TYPE_KEY
  const operatorOptions = isNil(sourceField) ? [] : getOperatorsForSource(sourceField)

  // A selection source's answers come from its taxonomy, so the value control
  // can offer them instead of asking the user to retype a category name. An
  // unsaved field has no taxonomy yet and carries its options on the row.
  useEffect(() => {
    if (!show || !isSelectionSource || isBlockedSource) {
      setSelectionOptions([])
      return undefined
    }
    // An edit applied for testing is what this field's options actually are on
    // screen, so the picker offers those rather than the saved taxonomy it is
    // about to replace. An option added in that edit has no uuid yet, so the
    // gate holds its name until the save mints one.
    const pendingChoices = getPendingOptionChoices(sourceField, pendingTaxonomies)
    if (!isNil(pendingChoices)) {
      setSelectionOptions(pendingChoices)
      return undefined
    }
    // An unsaved field's options have no category uuid yet — the taxonomy is
    // minted on save — so the gate holds the option's name and the save swaps in
    // the uuid the mint returns (see resolveStagedOperands).
    if (hasStagedOptions(sourceField)) {
      setSelectionOptions(
        sourceField.new_taxonomy_options.map((option) => ({
          value: option.name,
          label: option.name,
        }))
      )
      return undefined
    }
    if (isNil(sourceField.taxonomy_uuid)) {
      setSelectionOptions([])
      return undefined
    }

    let active = true
    setLoadingOptions(true)
    setOptionsErrored(false)
    dispatch(fetchTaxonomyOptions(sourceField.taxonomy_uuid))
      .then((result) => {
        if (active) {
          setSelectionOptions(
            result.options.map((option) => ({
              value: option.taxonomy_uuid,
              label: option.name,
            }))
          )
        }
      })
      .catch((err) => {
        console.error(err)
        if (active) {
          setOptionsErrored(true)
        }
      })
      .finally(() => {
        if (active) {
          setLoadingOptions(false)
        }
      })

    return () => {
      active = false
    }
  }, [show, sourceKey, isSelectionSource, pendingTaxonomies])

  const handleSourceChange = (option) => {
    setSourceKey(option?.value ?? '')
    setCompareValue('')
    // The new source may not allow the operator that was picked for the old one.
    const nextField = sourceOptions.find((field) => field.key === option?.value)
    const allowed = isNil(nextField) ? [] : getOperatorsForSource(nextField)
    if (!allowed.some((choice) => choice.value === operator)) {
      setOperator(allowed[0]?.value ?? EDGE_OPERATOR.eq)
    }
  }

  const handleOperatorChange = (option) => {
    setOperator(option?.value ?? EDGE_OPERATOR.eq)
    setCompareValue('')
  }

  const needsValue = operatorNeedsValue(operator)
  const isMultiValue = operator === EDGE_OPERATOR.in
  const selectedValues = isMultiValue ? parseOperandList(compareValue) : []
  const hasValue = isMultiValue ? !isEmpty(selectedValues) : !isEmpty(String(compareValue).trim())
  const canSave = !isNil(sourceField) && !isBlockedSource && (!needsValue || hasValue)

  const handleSave = () => {
    onSave({
      source: sourceField.key,
      target: targetField.key,
      operator,
      compare_value: needsValue ? compareValue : null,
    })
  }

  const renderValueControl = () => {
    if (!needsValue) {
      return null
    }

    if (isSelectionSource) {
      if (loadingOptions) {
        return <Loader />
      }
      if (optionsErrored) {
        return <ErrorMessage content="Could not load this field's options." />
      }
      if (isEmpty(selectionOptions)) {
        return <ErrorMessage content={`${sourceField.key} has no options to gate on yet.`} />
      }
      const options = selectionOptions
      return (
        <Select
          options={options}
          // react-select matches on option objects, not bare strings: handing it
          // the raw operand list renders label-less chips and leaves the menu
          // checkboxes unticked. Mapped rather than filtered against `options`
          // so a value whose taxonomy option has since gone still shows up and
          // can be removed.
          value={
            isMultiValue
              ? selectedValues.map((item) => ({
                  value: item,
                  label: options.find((option) => option.value === item)?.label ?? item,
                }))
              : compareValue
          }
          isMulti={isMultiValue}
          onChange={(option) =>
            setCompareValue(
              isMultiValue
                ? serializeOperandList((option ?? []).map((choice) => choice.value))
                : option?.value ?? ''
            )
          }
          placeholder={isMultiValue ? 'Select options' : 'Select an option'}
          isClearable={false}
          inputId="field-condition-value"
        />
      )
    }

    if (isMultiValue) {
      return (
        <CreatableInput
          inputId="field-condition-value"
          nameKey="condition-values"
          value={selectedValues.map((name) => ({ value: name, label: name }))}
          handleUpdateFilters={(unusedKey, nextOptions) =>
            setCompareValue(serializeOperandList(nextOptions.map((option) => option.value)))
          }
          placeholder="Type a value, then press Enter"
        />
      )
    }

    if (sourceTypeKey === BOOLEAN_TYPE_KEY) {
      return (
        <Select
          options={BOOLEAN_OPERAND_OPTIONS}
          value={compareValue}
          onChange={(option) => setCompareValue(option?.value ?? '')}
          placeholder="Select a value"
          isClearable={false}
          inputId="field-condition-value"
        />
      )
    }

    return (
      <TextInput
        id="field-condition-value"
        type={sourceTypeKey === 'integer' ? 'number' : 'text'}
        value={compareValue}
        onChange={(event) => setCompareValue(event.target.value)}
        placeholder={sourceTypeKey === 'integer' ? 'e.g. 500' : 'e.g. Billing'}
        data-testid="field-condition-value"
      />
    )
  }

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      title={
        isLogicField(targetField)
          ? `Condition for ${targetField.key}`
          : `Only run ${targetField.key} when…`
      }
      size="tiny"
    >
      <div className="rtn-condition">
        <div className="rtn-condition__section">
          <label className="rtn-condition__label" htmlFor="field-condition-source">
            Field
          </label>
          <Select
            inputId="field-condition-source"
            options={sourceOptions.map((field) => {
              const reason = getGateBlockedReason(field, inheritedTaxonomyKeys)
              return {
                value: field.key,
                label: field.key,
                // Shown greyed with the reason underneath rather than dropped
                // from the list — a missing field reads as a bug, and the fix
                // (fork the options) isn't guessable.
                isDisabled: !isNil(reason),
                ...(isNil(reason) ? {} : { description: reason }),
              }
            })}
            value={sourceKey}
            onChange={handleSourceChange}
            placeholder="Select a field"
            isClearable={false}
          />
        </div>

        {(isBlockedSource || everySourceBlocked) && (
          <Banner
            dataTestId="field-condition-blocked-banner"
            caution
            header={
              isBlockedSource
                ? `${sourceField.key} can't be used as a condition`
                : 'No field here can be used as a condition'
            }
          >
            <p>
              {isBlockedSource ? `${sourceField.key} uses` : 'These fields use'} options from your
              organization defaults, so a change there could remove the option a condition reads -
              and the defaults editor has no way to warn you. Break{' '}
              {isBlockedSource ? sourceField.key : 'a field'} from the defaults in its options
              editor to gate on it.
            </p>
          </Banner>
        )}

        <div className="rtn-condition__section">
          <label className="rtn-condition__label" htmlFor="field-condition-operator">
            Condition
          </label>
          <Select
            inputId="field-condition-operator"
            options={operatorOptions}
            value={operator}
            onChange={handleOperatorChange}
            isClearable={false}
            isDisabled={isNil(sourceField) || isBlockedSource}
          />
        </div>

        {needsValue && !isBlockedSource && (
          <div className="rtn-condition__section">
            <label className="rtn-condition__label" htmlFor="field-condition-value">
              {isMultiValue ? 'Values' : 'Value'}
            </label>
            {renderValueControl()}
          </div>
        )}

        <p className="muted-text" data-testid="field-condition-hint">
          Otherwise this field is skipped, along with anything that depends on it.{' '}
          {targetField.show_to_user === true
            ? 'Only fields shown to the agent are offered.'
            : 'Fields from either group are offered.'}
        </p>

        <div className="modal-footer">
          {!isNil(existingLink) && (
            <Button basic type="button" onClick={onRemove} data-testid="field-condition-remove">
              Remove
            </Button>
          )}
          <Button type="button" onClick={onClose} data-testid="field-condition-cancel">
            Cancel
          </Button>
          <Button
            brand
            type="button"
            disabled={!canSave}
            disabledTooltip={
              isBlockedSource ? blockedReason : 'Pick a field, a condition, and a value'
            }
            onClick={handleSave}
            data-testid="field-condition-save"
          >
            {isNil(existingLink) ? 'Add condition' : 'Update condition'}
          </Button>
        </div>
      </div>
    </BasicModal>
  )
}
